// The way in: sign in, and the MFA challenge a login may answer with (#231, #414).
//
// The form posts to a route that sets the session cookie server-side
// (`routes/auth.py`), so it stores nothing: a success is followed by
// `GET /auth/session`, and what that answers is the console's whole idea of
// who you are.
//
// **The MFA challenge is answered, not dropped.** `POST /auth/login` may come
// back `{"mfa_required": true, "mfa_token": ..., "methods": [...]}`, and the
// route that completes it is `POST /auth/mfa/complete` — see `api/session.ts`
// for why that is not the `/auth/mfa/verify` #231's text names.
//
// **There is no "create an account" here** (owner, 2026-09-12, on the Phase 3
// cut's Q2): the product is invitation-only for now, and an invitation's link
// is the way in for someone with no account. The register form #231 drew is
// gone from this screen; the route it posted to stays, for the invitation
// landing page (#415) to use with a token. Password recovery is `/forgot`,
// one of the doors (`Doors.tsx`).

import { useRef, useState, type FormEvent } from "react";

import type { PasskeyChallenge } from "../api/passkeys";
import { messageOf } from "../api/http";
import { passkeyLoginCompletion } from "./passkey";
import { Centre, Field, Notice } from "./chrome";

export function SignIn({
  notice,
  onSignIn,
}: {
  notice: string | null;
  onSignIn: (credentials: { email: string; password: string }) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const passwordInput = useRef<HTMLInputElement>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onSignIn({ email, password });
    } catch (failure) {
      setError(messageOf(failure));
      passwordInput.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centre title="Sign in" lede="Investigations, evidence and the decisions they led to.">
      {notice === null ? null : <Notice>{notice}</Notice>}
      <form onSubmit={submit} aria-busy={busy}>
        <Field
          id="email"
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Field
          id="password"
          ref={passwordInput}
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={error}
        />
        <div className="actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <a className="link link--muted" href="/forgot">
            Forgotten your password?
          </a>
        </div>
      </form>
      <p className="muted">
        <small>New here? Torque Assist is invitation-only for now. Ask a colleague, or the
        platform, for an invitation.</small>
      </p>
    </Centre>
  );
}

/** A login's second factors as a person names them (#798): the wire's `totp` is a code. */
const METHOD_LABEL = new Map<string, { choice: string; field: string }>([
  ["totp", { choice: "Authenticator app", field: "Code from your authenticator app" }],
  ["recovery", { choice: "Recovery code", field: "One of your recovery codes" }],
  ["passkey", { choice: "Passkey", field: "Code" }],
]);

/** The option a person picks a factor by. A factor this screen does not know yet is still words. */
export function methodLabel(method: string): string {
  const known = METHOD_LABEL.get(method);
  if (known !== undefined) return known.choice;
  const readable = method.replace(/_/g, " ");
  return readable.charAt(0).toUpperCase() + readable.slice(1);
}

// The MFA challenge (#231, #636). The code factors submit a typed credential;
// the `passkey` method is the browser ceremony instead — `beginPasskeyLogin`
// mints the challenge and `passkeyLoginCompletion` answers it — so an account
// whose only factor is a passkey is not shown a code field it cannot fill.
export function MfaChallenge({
  mfaToken,
  methods,
  onComplete,
  beginPasskeyLogin,
}: {
  mfaToken: string;
  methods: string[];
  onComplete: (challenge: {
    mfa_token: string;
    method: string;
    credential: string;
  }) => Promise<void>;
  /** Mint the assertion challenge for a login's `mfa_token` (#636, D10). */
  beginPasskeyLogin?: (mfaToken: string) => Promise<PasskeyChallenge>;
}) {
  const [method, setMethod] = useState(methods[0] ?? "totp");
  const [credential, setCredential] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeInput = useRef<HTMLInputElement>(null);
  const passkey = method === "passkey";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await onComplete({ mfa_token: mfaToken, method, credential });
    } catch (failure) {
      setProblem(messageOf(failure));
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const usePasskey = async () => {
    if (!beginPasskeyLogin) {
      setProblem("This browser cannot use a passkey yet.");
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      const completion = await passkeyLoginCompletion(beginPasskeyLogin, mfaToken);
      if (completion === null) {
        setProblem("The passkey prompt was dismissed. Try again or use another factor.");
        return;
      }
      await onComplete(completion);
    } catch (failure) {
      setProblem(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centre
      title="One more step"
      lede={
        passkey
          ? "That account answers with a passkey. Use it to finish signing in."
          : "That account has a second factor. Enter its code to finish signing in."
      }
    >
      <form onSubmit={submit} aria-busy={busy}>
        {methods.length > 1 ? (
          <p className="field">
            <label htmlFor="mfa-method" className="field__label">
              Method
            </label>
            <select
              id="mfa-method"
              className="input"
              value={method}
              onChange={(event) => setMethod(event.target.value)}
            >
              {methods.map((name) => (
                <option key={name} value={name}>
                  {methodLabel(name)}
                </option>
              ))}
            </select>
          </p>
        ) : null}
        {passkey ? (
          <>
            {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
            <div className="actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => void usePasskey()}
              >
                {busy ? "Waiting for your passkey…" : "Use your passkey"}
              </button>
            </div>
          </>
        ) : (
          <>
            <Field
              id="mfa-code"
              ref={codeInput}
              label={methods.length > 1 ? "Code" : (METHOD_LABEL.get(method)?.field ?? "Code")}
              name="credential"
              autoComplete="one-time-code"
              inputMode={method === "totp" ? "numeric" : "text"}
              required
              value={credential}
              onChange={(event) => setCredential(event.target.value)}
              error={problem}
            />
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? "Checking…" : "Finish signing in"}
            </button>
          </>
        )}
      </form>
    </Centre>
  );
}
